import { isEmpty, merge } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModules } from 'hooks';
import services from 'services';
import { ApplicationModuleConfig, AuthManagerConfig, MainErrorItem } from 'types';
import { convertExceptionToJson, findModule } from 'utils';

import ErrorView from '../ErrorView/ErrorView';
import LoadingProgress from '../LoadingProgress/LoadingProgress';

import ApplicationModule, { ApplicationModuleProps } from './ApplicationModule';

type ModuleSetup = {
  module: ApplicationModuleConfig | null;
  mainModule: ApplicationModuleConfig | null;
};

type ModuleOidcState = {
  loading: boolean;
  error: MainErrorItem | null;
};

const useModuleOidc = (moduleKey: string) => {
  const [state, setState] = useState<ModuleOidcState>({ loading: true, error: null });
  const { routedModules } = useModules();
  const moduleSetup: ModuleSetup = useMemo(() => findModule(routedModules, moduleKey), [routedModules, moduleKey]);
  const effectRef = useRef({
    ...moduleSetup,
    oidcModuleId: moduleSetup.module?.id,
  });

  useEffect(() => {
    const { module, mainModule, oidcModuleId } = effectRef.current;
    if (!oidcModuleId || (isEmpty(module?.oidc) && isEmpty(mainModule?.oidc))) {
      setState({
        loading: false,
        error: null,
      });
      return undefined;
    }
    const oidcConfig = merge(
      {},
      mainModule?.oidc || {},
      (mainModule && module?.route.startsWith(mainModule.route) && module?.oidc) || {},
    ) as AuthManagerConfig;

    const authManager = services.appFrame.auth.createAuthManager(oidcConfig, oidcModuleId);
    if (!authManager) {
      setState({
        loading: false,
        error: null,
      });
      return undefined;
    }

    authManager
      .initialize()
      .then(() => {
        setState({
          loading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        setState({
          loading: false,
          error: {
            title: 'errors.module_authentication_error.title',
            message: 'errors.common_message',
            details: {
              type: 'AUTH_INIT_ERROR',
              timestamp: new Date(),
              error: convertExceptionToJson(error),
            },
          },
        });
      });

    return () => {
      services.appFrame.auth.destroyAuthManager(oidcModuleId);
    };
  }, []);

  return { ...state, ...effectRef.current };
};

const OidcApplicationModule = ({ moduleKey, ...props }: Omit<ApplicationModuleProps, 'oidc'>) => {
  const { t } = useTranslation();
  const { oidcModuleId, error, loading } = useModuleOidc(moduleKey);
  if (error) {
    return <ErrorView {...error} />;
  }

  if (loading) {
    return <LoadingProgress>{t('progress.authenticating')}</LoadingProgress>;
  }

  return <ApplicationModule moduleKey={moduleKey} {...props} oidcModuleId={oidcModuleId} />;
};

export default OidcApplicationModule;
